// assets/scripts/gsap.js
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'
import Lenis from 'lenis'
import { createIcons, icons } from 'lucide'

gsap.registerPlugin(ScrollTrigger, SplitText)

// ------------------------------------------------------------
// Globals / singletons
// ------------------------------------------------------------
let lenis = null
let ctx = null
let splitInstances = []

// ------------------------------------------------------------
// Lucide
// ------------------------------------------------------------
function initLucide (root = document) {
  // Replaces <i data-lucide="..."></i> with inline svg
  const nodes = root.querySelectorAll?.('[data-lucide]')
  if (!nodes || nodes.length === 0) return

  try {
    createIcons({
      icons,
      attrs: { 'stroke-width': '2' } // keep consistent look
    })
  } catch (e) {
    // fail silently – don't break page if lucide isn't available for some reason
  }
}

// ------------------------------------------------------------
// Lenis (smooth scroll) + ScrollTrigger sync
// ------------------------------------------------------------
function initLenis () {
  if (lenis) {
    lenis.off('scroll', ScrollTrigger.update)
    lenis.destroy()
    lenis = null
  }

  lenis = new Lenis({
    autoRaf: true,
    lerp: 0.14,
    duration: 1
  })

  window.lenis = lenis
  lenis.on('scroll', ScrollTrigger.update)
}

// ------------------------------------------------------------
// Cleanup (important when components load/on transitions)
// ------------------------------------------------------------
function killAll () {
  // Revert SplitText
  splitInstances.forEach(inst => {
    try { inst.revert() } catch (e) {}
  })
  splitInstances = []

  // Kill ScrollTrigger + tweens
  ScrollTrigger.getAll().forEach(t => t.kill(true))
  gsap.killTweensOf('*')

  // Kill gsap.context scope
  if (ctx) {
    try { ctx.revert() } catch (e) {}
    ctx = null
  }
}

// ------------------------------------------------------------
// Animations
// ------------------------------------------------------------
function initSplitText (scopeEl) {
  // IMPORTANT:
  // Use a *specific* selector so we don't accidentally split other text
  // (the “one-letter-per-line” bug happens when the wrong elements get split).
  const els = scopeEl.querySelectorAll?.('[data-split-text]')
  if (!els || els.length === 0) return

  // Wait for fonts to avoid layout jumps
  const ready = document.fonts?.ready ? document.fonts.ready : Promise.resolve()

  ready.then(() => {
    // small delay so layout settles (Flynt/lazy components)
    window.setTimeout(() => {
      els.forEach(el => {
        // Prevent double-splitting
        if (el.dataset.splitReady === '1') return
        el.dataset.splitReady = '1'

        // Make sure it's visible when animating
        gsap.set(el, { opacity: 1 })

        const split = SplitText.create(el, {
          type: 'lines',
          linesClass: 'line',
          autoSplit: true,
          // If you used mask before, keep it – otherwise remove it.
          mask: 'lines',
          onSplit: (self) => {
            gsap.from(self.lines, {
              duration: 1.2,
              yPercent: 105,
              opacity: 0,
              stagger: 0.12,
              ease: 'expo.out',
              delay: 0.05
            })
          }
        })

        splitInstances.push(split)
      })
    }, 120)
  })
}

function initFadeUpChildren (scopeEl) {
  const sections = gsap.utils.toArray(scopeEl.querySelectorAll?.('.fade-up-children') || [])
  sections.forEach(section => {
    const children = section.querySelectorAll('.animate')
    if (!children.length) return

    gsap.from(children, {
      scrollTrigger: {
        trigger: section,
        start: 'top 85%',
        once: true
      },
      y: 28,
      autoAlpha: 0,
      duration: 1.15,
      ease: 'power4.out',
      stagger: 0.12
    })
  })
}

function initParallax (scopeEl) {
  gsap.utils.toArray(scopeEl.querySelectorAll?.('.parallax-high:not([name="BlockBackgroundText"])') || [])
    .forEach(element => {
      const figureImage = element.querySelector('.figure-image')
      if (!figureImage) return

      gsap.fromTo(
        figureImage,
        { yPercent: 45 },
        {
          yPercent: -45,
          ease: 'none',
          scrollTrigger: {
            trigger: element,
            scrub: true
          }
        }
      )
    })

  gsap.utils.toArray(scopeEl.querySelectorAll?.('.parallax') || [])
    .forEach(element => {
      const figureImage = element.querySelector('.figure-image')
      if (figureImage) {
        gsap.fromTo(
          figureImage,
          { yPercent: -18 },
          {
            yPercent: 10,
            ease: 'none',
            scrollTrigger: {
              trigger: element,
              scrub: true
            }
          }
        )
      }

      const header = element.querySelector('.parallax-header')
      if (header) {
        gsap.fromTo(
          header,
          { yPercent: 0 },
          {
            yPercent: -22,
            ease: 'none',
            scrollTrigger: {
              trigger: element,
              start: 'top top',
              end: 'bottom top',
              scrub: true,
              pin: header,
              pinSpacing: false,
              pinType: 'transform',
              anticipatePin: 1
            }
          }
        )
      }
    })
}

function initPricingPackagesCards (scopeEl) {
  // Subtle “premium” reveal that matches your design:
  // fade + slight lift + slight scale. Staggered, once.
  const cards = gsap.utils.toArray(scopeEl.querySelectorAll?.("flynt-component[name='PricingPackages'] .pricingCard") || [])
  if (!cards.length) return

  gsap.set(cards, { willChange: 'transform,opacity' })

  gsap.from(cards, {
    scrollTrigger: {
      trigger: cards[0].closest('.pricingPackages') || cards[0],
      start: 'top 80%',
      once: true
    },
    y: 22,
    scale: 0.985,
    autoAlpha: 0,
    duration: 1.05,
    ease: 'power3.out',
    stagger: 0.12,
    clearProps: 'willChange'
  })
}

function initCalendarWidget (scopeEl) {
  const widgets = gsap.utils.toArray(scopeEl.querySelectorAll?.('.heroSplit__calendarWidget') || [])
  if (!widgets.length) return

  widgets.forEach(widget => {
    const card = widget.querySelector('.heroSplit__calendarCard')
    if (!card) return

    const header = card.querySelector('.heroSplit__calendarHeader')
    const dayNames = gsap.utils.toArray(card.querySelectorAll('.heroSplit__calendarDayName') || [])
    const meetingsSection = card.querySelector('.heroSplit__calendarMeetings')
    const meetingCards = gsap.utils.toArray(card.querySelectorAll('.heroSplit__calendarMeeting') || [])

    // Card entrance
    gsap.from(card, {
      scrollTrigger: {
        trigger: widget,
        start: 'top 85%',
        once: true
      },
      y: 40,
      autoAlpha: 0,
      duration: 1,
      ease: 'power3.out',
      onComplete: () => {
        // After card is visible, stagger in children
        const tl = gsap.timeline()

        if (header) {
          tl.from(header, {
            y: 12,
            autoAlpha: 0,
            duration: 0.6,
            ease: 'power3.out'
          }, 0)
        }

        if (dayNames.length) {
          tl.from(dayNames, {
            autoAlpha: 0,
            y: 8,
            duration: 0.4,
            ease: 'power2.out',
            stagger: 0.03
          }, 0.1)
        }

        // Animate calendar cells row by row
        const cells = gsap.utils.toArray(card.querySelectorAll('.heroSplit__calendarCell') || [])
        if (cells.length) {
          tl.from(cells, {
            scale: 0.8,
            autoAlpha: 0,
            duration: 0.35,
            ease: 'back.out(1.4)',
            stagger: {
              each: 0.02,
              grid: 'auto',
              from: 'start'
            }
          }, 0.2)
        }

        if (meetingsSection) {
          tl.from(meetingsSection, {
            y: 16,
            autoAlpha: 0,
            duration: 0.6,
            ease: 'power3.out'
          }, 0.5)
        }

        if (meetingCards.length) {
          tl.from(meetingCards, {
            x: -20,
            autoAlpha: 0,
            duration: 0.5,
            ease: 'power3.out',
            stagger: 0.12
          }, 0.6)
        }
      }
    })
  })
}

function initCardHoverMicro (scopeEl) {
  // Optional hover micro-interaction (GSAP) – feels nice on desktop,
  // but stays totally harmless on mobile.
  const cards = gsap.utils.toArray(scopeEl.querySelectorAll?.('.pricingCard, .card, .careerCard') || [])
  if (!cards.length) return

  cards.forEach(card => {
    const isPopular = card.classList.contains('pricingCard--popular')
    const onEnter = () => {
      gsap.to(card, {
        y: isPopular ? -8 : -2,
        duration: 0.22,
        ease: 'power2.out'
      })
    }
    const onLeave = () => {
      gsap.to(card, {
        y: 0,
        duration: 0.22,
        ease: 'power2.out'
      })
    }

    card.addEventListener('mouseenter', onEnter)
    card.addEventListener('mouseleave', onLeave)

    // Store handlers for cleanup via context
    gsap.context(() => {}, card).add(() => {
      card.removeEventListener('mouseenter', onEnter)
      card.removeEventListener('mouseleave', onLeave)
    })
  })
}

// ------------------------------------------------------------
// Custom cursor
// ------------------------------------------------------------
let cursorEl = null
let cursorLabel = null
let cursorMoveX = null
let cursorMoveY = null

const CURSOR_DOT = 16
const CURSOR_BADGE_W = 110
const CURSOR_BADGE_H = 36

function initCursor () {
  if (!window.matchMedia('(pointer: fine)').matches) return

  if (!cursorEl) {
    cursorEl = document.createElement('div')
    cursorEl.classList.add('custom-cursor')
    cursorLabel = document.createElement('span')
    cursorLabel.classList.add('custom-cursor__label')
    cursorLabel.textContent = 'klicka här'
    cursorEl.appendChild(cursorLabel)
    document.body.appendChild(cursorEl)
  }

  // Always centered on pointer via xPercent/yPercent -50
  gsap.set(cursorEl, { xPercent: -50, yPercent: -50, x: -200, y: -200 })

  cursorMoveX = gsap.quickTo(cursorEl, 'x', { duration: 0.04, ease: 'none' })
  cursorMoveY = gsap.quickTo(cursorEl, 'y', { duration: 0.04, ease: 'none' })

  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseover', onLinkEnter)
  document.addEventListener('mouseout', onLinkLeave)
}

function onMouseMove (e) {
  cursorMoveX(e.clientX)
  cursorMoveY(e.clientY)
}

function isExternalLink (el) {
  const a = el.closest('a[href]')
  if (!a) return false
  try {
    const url = new URL(a.href)
    return url.hostname !== window.location.hostname
  } catch {
    return false
  }
}

function onLinkEnter (e) {
  if (!isExternalLink(e.target)) return
  gsap.to(cursorEl, {
    width: CURSOR_BADGE_W,
    height: CURSOR_BADGE_H,
    duration: 0.35,
    ease: 'back.out(1.5)',
    overwrite: 'auto'
  })
  gsap.to(cursorLabel, { opacity: 1, fontSize: 13, duration: 0.2, delay: 0.1, overwrite: 'auto' })
}

function onLinkLeave (e) {
  if (!isExternalLink(e.target)) return
  if (e.relatedTarget && isExternalLink(e.relatedTarget)) return
  // Kill all pending/delayed label tweens (including delayed onLinkEnter ones)
  // before starting collapse — prevents opacity:1 from firing after we hide it
  gsap.killTweensOf(cursorLabel)
  gsap.to(cursorLabel, { opacity: 0, fontSize: 0, duration: 0.12 })
  gsap.to(cursorEl, {
    width: CURSOR_DOT,
    height: CURSOR_DOT,
    duration: 0.25,
    ease: 'power3.out',
    overwrite: 'auto'
  })
}

// ------------------------------------------------------------
// Public init (call on first load + after page transitions)
// ------------------------------------------------------------
export function initGsap (root = document) {
  // 1) cleanup first (prevents duplicates)
  killAll()

  // 2) lenis
  initLenis()

  // cursor (once, idempotent)
  initCursor()

  // 3) lucide first (so icons exist before measurements/animations)
  initLucide(root)

  // 4) scope everything so it can be reverted cleanly
  ctx = gsap.context(() => {
    initSplitText(root)
    initFadeUpChildren(root)
    initParallax(root)
    initPricingPackagesCards(root)
    initCalendarWidget(root)
    initCardHoverMicro(root)
  }, root)

  // 5) refresh after a tick (Flynt lazy components / images)
  requestAnimationFrame(() => {
    ScrollTrigger.refresh()
  })
}

// Auto-init on normal page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initGsap(document))
} else {
  initGsap(document)
}

// If you ever do partial DOM swaps (Flynt load:on="visible", ajax, etc.),
// you can call this after injection:
// initLucide(newRoot); ScrollTrigger.refresh();
